import { ArrowRight, Circle } from 'lucide-react'
import { useCallback, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  createOrganisation,
  type CreateOrganisationInput
} from '@/features/onboarding/services/onboardingService.http'
import { fetchMe, registerAccount, signIn } from '@/features/auth/slices/sessionSlice'
import { useAppDispatch } from '@/store'

/**
 * `/onboarding/01` (PX-W6.1) — onboarding step 01, "create the environment":
 * a two-step form in ONE page, outside `AppShell` and unguarded (the third
 * unguarded surface beside `/login` and `/oauth/consent`).
 *
 * Step 1 — register `{name, email, password}`. The backend returns the user
 * only (no token, D8), so the page immediately signs the same user in
 * (`signIn`), which lands the session's roleKey + first membership.
 *
 * Step 2 — create the first organisation `{slug, name, region,
 * dataClassification}`. The slug is client-validated against `^[a-z0-9-]+$`
 * before submit; provisioning rides the backend's first-org bootstrap arm.
 * On success `fetchMe` refreshes the memberships (so `activeOrgId` exists and
 * the WebSocket can arm) and the page routes into the app.
 *
 * Credentials (I-1): the password lives in one local `useState`, goes to
 * register + login only, and is cleared on each submit. Errors render through
 * the `errors:` prefix rule with the generic fallback (§6.4).
 */

const SLUG_PATTERN = /^[a-z0-9-]+$/

type Step = 'register' | 'create-org'

export default function OnboardingStep01Page() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation(['onboarding', 'auth', 'errors'])

  const [step, setStep] = useState<Step>('register')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [slug, setSlug] = useState('')
  const [orgName, setOrgName] = useState('')
  const [region, setRegion] = useState('')
  const [dataClassification, setDataClassification] = useState('')

  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [slugRejected, setSlugRejected] = useState(false)
  const [busy, setBusy] = useState(false)

  const onRegisterSubmitted = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (name.trim().length === 0) return setErrorKey('user.name.required')
      if (email.trim().length === 0) return setErrorKey('user.email.required')
      if (password.length === 0) return setErrorKey('auth.register.password-required')
      setPassword('')
      setBusy(true)
      try {
        await dispatch(registerAccount({ name: name.trim(), email: email.trim(), password })).unwrap()
        // The backend mints no token on register (D8): sign in immediately.
        await dispatch(signIn({ email: email.trim(), password })).unwrap()
        setErrorKey(null)
        setOrgName((current) => (current === '' ? name.trim() : current))
        setStep('create-org')
      } catch (rejected) {
        setErrorKey(backendKeyOf(rejected))
      } finally {
        setBusy(false)
      }
    },
    [name, email, password, dispatch]
  )

  const onOrgSubmitted = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (!SLUG_PATTERN.test(slug)) {
        setSlugRejected(true)
        return
      }
      if (orgName.trim().length === 0) return setErrorKey('organisation.name.required')
      if (region.trim().length === 0) return setErrorKey('tenant.region.required')
      if (dataClassification.trim().length === 0) {
        return setErrorKey('tenant.classification.required')
      }
      setBusy(true)
      try {
        const input: CreateOrganisationInput = {
          slug,
          name: orgName.trim(),
          region: region.trim(),
          dataClassification: dataClassification.trim()
        }
        await createOrganisation(input)
        // Refresh the session so the new membership lands (activeOrgId exists,
        // the WebSocket can arm, the role derives `admin`), then enter the app.
        await dispatch(fetchMe()).unwrap()
        navigate('/')
      } catch (rejected) {
        setErrorKey(backendKeyOf(rejected))
      } finally {
        setBusy(false)
      }
    },
    [slug, orgName, region, dataClassification, dispatch, navigate]
  )

  const fieldError = errorKey
    ? t(`errors:${errorKey}`, { defaultValue: t('errors:generic') })
    : null

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <div className="mx-auto flex w-full max-w-[72rem] flex-1 flex-col justify-center px-6 py-10">
        <div className="grid grid-cols-2 items-start gap-10">
          <div className="sticky top-10 flex flex-col pt-2">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary font-serif text-base font-bold text-primary-foreground">
                {t('auth:brand.mark')}
              </span>
              <span className="text-sm font-medium text-ink-soft">
                {t('auth:brand.wordmark')}
              </span>
            </div>
            <h1 className="mt-6 max-w-lg font-serif text-3xl font-semibold leading-tight text-foreground">
              {t('onboarding:step01.heading')}
            </h1>
            <p className="mt-3 max-w-lg text-sm text-muted-foreground">
              {t('onboarding:step01.lede')}
            </p>
            <dl className="mt-6 grid max-w-lg gap-3">
              {(
                t('auth:values', { returnObjects: true }) as unknown as {
                  title: string
                  detail: string
                }[]
              ).map((value) => (
                <div key={value.title} className="flex items-start gap-2">
                  <span
                    aria-hidden
                    className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"
                  >
                    <Circle className="h-2 w-2 fill-current" />
                  </span>
                  <div>
                    <dt className="text-sm font-medium text-foreground">{value.title}</dt>
                    <dd className="text-xs text-muted-foreground">{value.detail}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-lg border border-border bg-card px-6 py-6">
            {step === 'register' ? (
              <>
                <h2 className="font-serif text-xl font-semibold text-foreground">
                  {t('onboarding:step01.register.heading')}
                </h2>
                <form onSubmit={onRegisterSubmitted} noValidate>
                  <div className="mt-4 grid gap-3">
                    <label className="block">
                      <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('onboarding:step01.register.nameLabel')}
                      </span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value)
                          setErrorKey(null)
                        }}
                        aria-label={t('onboarding:step01.register.nameLabel')}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('onboarding:step01.register.emailLabel')}
                      </span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value)
                          setErrorKey(null)
                        }}
                        autoComplete="username"
                        aria-label={t('onboarding:step01.register.emailLabel')}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('onboarding:step01.register.passwordLabel')}
                      </span>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value)
                          setErrorKey(null)
                        }}
                        aria-label={t('onboarding:step01.register.passwordLabel')}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                      />
                    </label>
                  </div>

                  {fieldError && (
                    <p role="alert" className="mt-2 text-xs font-medium text-blocked">
                      {fieldError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={busy}
                    aria-busy={busy}
                    className="mt-4 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                  >
                    {busy
                      ? t('onboarding:step01.register.submitting')
                      : t('onboarding:step01.register.submit')}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="font-serif text-xl font-semibold text-foreground">
                  {t('onboarding:step01.org.heading')}
                </h2>
                <form onSubmit={onOrgSubmitted} noValidate>
                  <div className="mt-4 grid gap-3">
                    <label className="block">
                      <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('onboarding:step01.org.slugLabel')}
                      </span>
                      <input
                        type="text"
                        value={slug}
                        onChange={(e) => {
                          setSlug(e.target.value)
                          setSlugRejected(false)
                          setErrorKey(null)
                        }}
                        aria-label={t('onboarding:step01.org.slugLabel')}
                        aria-invalid={slugRejected}
                        aria-describedby={slugRejected ? 'slug-hint' : undefined}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 font-mono text-sm text-foreground"
                      />
                      <span
                        id="slug-hint"
                        className={slugRejected ? 'mt-1 block text-xs text-blocked' : 'mt-1 block text-xs text-muted-foreground'}
                      >
                        {t('onboarding:step01.org.slugHint')}
                      </span>
                    </label>
                    <label className="block">
                      <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('onboarding:step01.org.nameLabel')}
                      </span>
                      <input
                        type="text"
                        value={orgName}
                        onChange={(e) => {
                          setOrgName(e.target.value)
                          setErrorKey(null)
                        }}
                        aria-label={t('onboarding:step01.org.nameLabel')}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('onboarding:step01.org.regionLabel')}
                        </span>
                        <input
                          type="text"
                          value={region}
                          onChange={(e) => {
                            setRegion(e.target.value)
                            setErrorKey(null)
                          }}
                          aria-label={t('onboarding:step01.org.regionLabel')}
                          className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                        />
                      </label>
                      <label className="block">
                        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          {t('onboarding:step01.org.classificationLabel')}
                        </span>
                        <input
                          type="text"
                          value={dataClassification}
                          onChange={(e) => {
                            setDataClassification(e.target.value)
                            setErrorKey(null)
                          }}
                          aria-label={t('onboarding:step01.org.classificationLabel')}
                          className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground"
                        />
                      </label>
                    </div>
                  </div>

                  {fieldError && (
                    <p role="alert" className="mt-2 text-xs font-medium text-blocked">
                      {fieldError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={busy}
                    aria-busy={busy}
                    className="mt-4 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                  >
                    {busy
                      ? t('onboarding:step01.org.submitting')
                      : t('onboarding:step01.org.submit')}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Pull the i18n-mappable key out of a thunk/service rejection (§6.4). */
function backendKeyOf(rejected: unknown): string {
  if (typeof rejected === 'object' && rejected !== null && 'backendKey' in rejected) {
    const key = (rejected as { backendKey?: unknown }).backendKey
    if (typeof key === 'string' && key.length > 0) return key
  }
  return 'generic'
}
